import type { EnhancedStore } from '@reduxjs/toolkit';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import axiosBetterStacktrace from 'axios-better-stacktrace';
import assign from 'lodash/assign';

import { nullifyEmptyStringAttributes, parseIncludes } from 'src/app/popsicle/client/helpers';
import { withErrorHandling } from 'src/app/popsicle/client/redux';
import type {
  Cart,
  PatientDependentDetails,
  UpdateCartDeliveryAddressRequest,
  UpdateCartDeliveryMethodRequest,
  UpdateCartPaymentMethodRequest,
} from 'src/app/types';
import { isHydra } from 'src/app/utils/browser';
import { VERSION } from 'src/consts';

import {
  bffActiveHomeItemsUrl,
  bffActivePendingItemsUrl,
  bffCartUrl,
  bffCheckoutCartUrl,
  bffCreateCartUrl,
  bffGetOrderDetailUrl,
  bffGetOrderIdFromShortCodeUrl,
  bffGetOTCItemsUrl,
  bffGetRescheduledDeliveryWindowsForOrder,
  checkEmailAvailabilityUrl,
  checkZipCodeEligibilityUrl,
  getMedicationsV3Url,
  notifyTransferUrl,
  onboardingUrl,
  onboardingValidateUrl,
  onboardingWithMatchUrl,
  ordersUrl,
  postInsuranceCardUrl,
} from './api';

const headers = {
  'Content-Type': 'application/vnd.api+json',
  'Capsule-Client': 'web',
  'Capsule-Client-Version': VERSION,
  tz: Intl.DateTimeFormat().resolvedOptions().timeZone, // eslint-disable-line
};

// Also defined in ~/SentryConfig.js - adjust there if this value changes
const AXIOS_TIMEOUT = 5 * 60 * 1000; // 5 minutes time-out, let the server time out, but slow connections can continue

const axiosConfig = {
  contentType: 'application/vnd.api+json',
  accept: 'application/vnd.api+json',
  headers,
  timeout: AXIOS_TIMEOUT,
};

const v3AxiosConfig: AxiosRequestConfig = {
  headers: {
    'Capsule-Client': 'web',
    'Capsule-Client-Version': VERSION,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone, // eslint-disable-line
  },
  timeout: AXIOS_TIMEOUT,
};

interface BuildClientArgs {
  store: any;
  namespace?: any;
}

//This is exported to appease isolatedModules. Do not import this, but - also - don't delete the export keyword.
export class Client {
  namespace: string;
  store: any;
  that: any;
  makeActions: any;
  makeClients: any;
  _clients: any;
  constructor(
    store: EnhancedStore,
    namespace: string,
    makeClients: () => { [clientName: string]: AxiosInstance },
    makeActions: (a: { [clientName: string]: AxiosInstance }) => any
  ) {
    this.namespace = namespace;
    this.store = store;
    this.makeActions = makeActions;
    this.makeClients = makeClients;

    this._clients = makeClients();

    const actions = makeActions(this._clients);

    assign(this, actions);
  }
}

export default function buildClient({ store, namespace }: BuildClientArgs) {
  // Helper function to make requestBuilder from an axios client
  const addClientInterceptors = (client: AxiosInstance) => {
    client.interceptors.request.use(async config => {
      if (isHydra()) {
        config.headers = {
          ...config.headers,
          hydra: true,
        };
      }

      // First, attempt to retrieve a jwt from amplify
      try {
        const { accessToken } = (await fetchAuthSession()).tokens ?? {}; // Refreshes the token if the refresh token is still valid.
        const jwt = accessToken?.toString();

        if (jwt) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${jwt}`,
          };
        }
      } catch (e) {
        // noop
      }

      return config;
    });

    client.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error?.response?.data?.errorCode === 666) {
          window.location.replace('/maintenance');
          return;
        } else if (error?.response?.status === 401) {
          console.log('Received a 401, signing out.');
          signOut();
          return;
        }
        return Promise.reject(error);
      }
    );
    return client;
  };

  const makeResource = (endpoint: string, axiosInstance: AxiosInstance) => {
    return {
      get: (id: string, includes = [], query: any = {}) => {
        query['include'] = parseIncludes(includes);
        return axiosInstance.get(`${endpoint}/${id}`, {
          params: query,
        });
      },

      list: (includes = [], query: any = {}, page = 1, page_size = 50) => {
        query['page'] = page;
        query['page_size'] = page_size;
        query['include'] = parseIncludes(includes);
        return axiosInstance.get(`${endpoint}`, { params: query });
      },

      create: (params: any, includes = [], query: any = {}) => {
        query['include'] = parseIncludes(includes);
        return axiosInstance.post(`${endpoint}`, nullifyEmptyStringAttributes(params), {
          params: query,
        });
      },

      patch: (id: string, params: any, includes = [], query: any = {}) => {
        query['include'] = parseIncludes(includes);
        return axiosInstance.patch(`${endpoint}/${id}`, nullifyEmptyStringAttributes(params), {
          params: query,
        });
      },

      delete: (id: string, includes = [], query: any = {}) => {
        query['include'] = parseIncludes(includes);
        return axiosInstance.delete(`${endpoint}/${id}`, {
          params: query,
        });
      },

      related: (id: string, related: string, includes = [], query: any = {}, page = 1, page_size = 50) => {
        query['page'] = page;
        query['page_size'] = page_size;
        query['include'] = parseIncludes(includes);
        return axiosInstance.get(`${endpoint}/${id}/${related}`, {
          params: query,
        });
      },
    };
  };

  const client = new Client(
    store,
    namespace,
    () => {
      const clients = {
        pharmakonClientV2: axios.create(
          Object.assign({}, axiosConfig, {
            baseURL: `${process.env.NEXT_PUBLIC_BFF_ENDPOINT}/proxy/v2`,
          })
        ),
        pharmakonClientV3: axios.create(
          Object.assign({}, v3AxiosConfig, {
            baseURL: `${process.env.NEXT_PUBLIC_BFF_ENDPOINT}/proxy/v3`,
          })
        ),
        bffClient: axios.create({ ...axiosConfig, baseURL: process.env.NEXT_PUBLIC_BFF_ENDPOINT }),
      };

      Object.values(clients).forEach(client => axiosBetterStacktrace(client));

      return clients;
    },

    ({ pharmakonClientV2, pharmakonClientV3, bffClient }: { [clientName: string]: AxiosInstance }) => {
      const result: any = {};

      const pharmakon = {
        v2: addClientInterceptors(pharmakonClientV2),
        v3: addClientInterceptors(pharmakonClientV3),
      };

      const bff = addClientInterceptors(bffClient);

      result.changePassword = (params: any) =>
        withErrorHandling(store, () => pharmakon.v2.post('account/password_change', params));

      result.changePasswordWithApiKey = (password: string) =>
        withErrorHandling(store, () => pharmakon.v3.post('auth/user/password_change', { password }));

      result.user = makeResource('auth/user', pharmakon.v2);

      result.account = assign(makeResource('account', pharmakon.v2), {
        create: async (params: any, includes = []) =>
          await pharmakon.v2.post('account', params, {
            params: { include: parseIncludes(includes) },
          }),
        homeScreen: (id: string) => {
          return pharmakon.v2.get(`account/${id}/home_screen`);
        },
        homeScreenArchived: (id: string) => pharmakon.v2.get(`account/${id}/home_screen_archived`),
        deleteAccountAddress: (addressId: string, body: any) =>
          pharmakon.v2.delete(`delivery_address/${addressId}`, {
            data: body,
          }),
        onboarding: (params: any) => bff.post(onboardingUrl(), params),
        onboardingWithMatch: (params: any) => bff.post(onboardingWithMatchUrl(), params),
        onboardingValidate: (params: any) => bff.post(onboardingValidateUrl(), params),
        notifyTransfer: async (
          accountId: string // TODO business logic to call this fn must be moved to the backend.
        ) => await pharmakon.v2.post(`account/${accountId}/notify_transfer`),
        onboardingUpdate: (accountId: string, body: any) => pharmakon.v3.patch(`/onboarding/${accountId}`, body),
        obtainMagicToken: () => pharmakon.v2.get('account/obtain_magic_token'),
        transferPromptSeen: async (accountId: string) =>
          await pharmakon.v2.patch(`account/${accountId}`, {
            data: {
              attributes: {
                should_show_transfer_prompt: false,
              },
              type: 'Account',
              id: accountId,
            },
          }),
      });

      result.vestoSubmissions = (params: any) => {
        return bff.post(`api/form`, params);
      };

      result.accountFacility = async (accountId: string) => {
        return await bff.get(`api/accounts/${accountId}/facility`);
      };

      result.patient = makeResource('patient', pharmakon.v2);

      result.patient.currentlyLoggedInPatientHealthkitRecordsAvailable = async () => {
        const response = await withErrorHandling(store, () => pharmakon.v2.get('mobile-deep-link'));
        return response.data.data.attributes.healthkit_records_available;
      };

      result.addPatientDependent = (accountId: string, body: PatientDependentDetails) =>
        bff.post(`api/accounts/${accountId}/patients`, body);

      result.patient.updatePatient = (accountId: string, patientId: string, values: object) => {
        return bff.patch(`api/accounts/${accountId}/patients/${patientId}`, values);
      };

      result.insuranceLookup = makeResource('insurance_lookup', pharmakon.v2);
      result.prescription = makeResource('prescription', pharmakon.v2);

      result.prescriptionTransfer = assign(makeResource('prescription_transfer', pharmakon.v2), {
        create: (data: any, dob: string, sex: string) =>
          pharmakon.v2.post(`prescription_transfer`, data, {
            params: {
              dob,
              sex,
            },
          }),
      });

      result.doctorTransfers = (body: any) => bff.post('api/transfers/doctorTransfers', body);

      result.transferPrescription = (body: any) => bff.post('api/transfers/prescriptions', body);
      result.updatePrescriptionTransfer = (patientId: string, transfer: any) =>
        bff.patch(`api/transfers/prescriptions/${transfer.id}`, { patientId, ...transfer });
      result.prescriptionFulfillment = makeResource('prescription_fulfillment', pharmakon.v2);
      result.deliveryAddress = makeResource('delivery_address', pharmakon.v2);
      result.deliveryWindow = makeResource('delivery_window', pharmakon.v2);
      result.deliveryZone = assign(makeResource('deliverable_zip_codes', pharmakon.v2), {
        listZipCodes: () => pharmakon.v2.get('delivery_zone/list_deliverable_zip_codes'),
      });

      result.product = makeResource('product', pharmakon.v2);

      result.cart = assign(makeResource('cart', pharmakon.v2), {
        get: (cartId: string, includes = []) =>
          pharmakon.v2.get(`/cart/${cartId}`, {
            params: { include: parseIncludes(includes) },
          }),
      });

      //todo fix this guy to use bff. search for client.order
      result.order = assign(makeResource('order', pharmakon.v2), {
        listPlaced: (includes = []) =>
          pharmakon.v2.get('order', {
            params: {
              include: parseIncludes(includes),
              ordering: '-created_at',
              status_in: 'processing,delivering,completed',
              page_size: '10000',
            },
          }),
        process: (id: string, params: any, includes = []) =>
          pharmakon.v2.post(`order/${id}/process`, params, {
            params: parseIncludes(includes),
          }),
        availableDeliveryWindows: (id: string) => pharmakon.v2.get(`order/${id}/available_delivery_windows`),
        status: (id: string) => pharmakon.v2.get(`order/${id}/status`),
      });
      result.orderItem = makeResource('order_item', pharmakon.v2);
      result.bffOrders = () => bff.get(ordersUrl());
      result.bffGetOrderIdFromShortCode = (shortCode: string) => bff.get(bffGetOrderIdFromShortCodeUrl(shortCode));
      result.bffGetOrderDetail = (orderId: string) => bff.get(bffGetOrderDetailUrl(orderId));
      result.getRescheduledDeliveryWindows = (orderId: string) => {
        return bff.get(bffGetRescheduledDeliveryWindowsForOrder(orderId));
      };
      result.patchRescheduledOrder = (orderId: string, payload: { deliveryWindowUUID: string; accountId: string }) =>
        bff.patch(bffGetOrderDetailUrl(orderId), payload);
      result.validateAddress = (body: any) => pharmakon.v2.post('/validate_delivery_address', body);

      result.googlePlaces = {};
      result.googlePlaces.autocomplete = (input: any) =>
        pharmakon.v2.get('proxy/google/autocomplete', {
          params: { input },
        });

      result.googlePlaces.details = (place_id: string) =>
        pharmakon.v2.get('/proxy/google/details', {
          params: { place_id },
        });

      result.placeAutoComplete = (params: Partial<Record<string, any>> & { input: string }) =>
        bff.get(`/api/search/place/autocomplete`, { params });

      result.textSearch = (params = {}) => bff.get(`/api/search/place/textsearch`, { params });

      result.placePhoto = (params = {}) => bff.get(`/api/search/place/photo`, { params });

      result.placeDetails = (params = {}) => bff.get(`/api/search/place/details`, { params });

      result.getInsurance = (accountId: string) => bff.get(`/api/v2/insurance?accountId=${accountId}`);

      result.deleteInsurance = (insuranceId: string, patientId: string) =>
        bff.delete(`/api/v2/insurance/${insuranceId}/patients/${patientId}`);

      result.pendingInsurance = (accountId: string) => pharmakon.v2.get(`insurance/pending/account/${accountId}`);

      result.postInsuranceCard = (primary_patient_id: string, frontFile: any, backFile: any) => {
        const data = new FormData();
        data.append('front', frontFile);
        data.append('back', backFile);
        return bff.post(postInsuranceCardUrl(primary_patient_id), data);
      };

      result.deleteInsuranceCard = (id: string, patient_id: string) =>
        pharmakon.v2.delete(`insurance/patient/${patient_id}/${id}`);

      result.medicationSetArchived = (patientMedicationId: string, archive: boolean) =>
        bff.patch(`api/patientMedications/${patientMedicationId}/is_archived`, { isArchived: archive });

      result.fetchProviders = (name: string) =>
        withErrorHandling(store, () =>
          bff.get('/api/search/providers', {
            params: { provider_name: name },
          })
        );

      result.fetchAccountProvidersByPatient = (accountId: string) => bff.get(`api/providers/${accountId}/patients`);
      result.bffPendingTransfers = (id: string) =>
        bff.get(`/api/home/${id}/pending_items`, {
          params: { type: 'transfer' },
        });
      result.bffActiveAndPendingMedications = (id: string) => bff.get(bffActivePendingItemsUrl(id));
      result.bffActiveHomeItems = (id: string) => bff.get(bffActiveHomeItemsUrl(id));
      result.bffArchivedMedications = (id: string) => bff.get(`/api/v3/archived_medications/${id}`);
      result.bffAccount = (id: string) => bff.get(`/api/accounts/${id}`);
      result.bffAccountUploadPhotoId = (accountId: string, photoId: any) => {
        const data = new FormData();
        data.append('identification_photo', photoId);
        return bff.post(`/api/accounts/${accountId}/identification_photo`, data);
      };

      result.bffGetPaymentCards = (accountId: string) => bff.get(`api/accounts/${accountId}/payment_methods`);
      result.bffCreatePaymentCard = (accountId: string, body: { cardToken: string }) =>
        bff.post(`api/accounts/${accountId}/payment_methods`, body);
      result.bffDeletePaymentCard = (accountId: string, paymentMethodId: string) =>
        bff.delete(`api/accounts/${accountId}/payment_methods/${paymentMethodId}`);

      result.bffPatient = (id: string) => bff.get(`/api/accounts/${id}/patients`);
      result.bffDeliveryAddress = (id: string) => bff.get(`/api/accounts/${id}/delivery_addresses`);
      result.bffGetLegacyInsurance = (patientIdsString: string) => {
        return bff.get(`api/insurance/legacy?pharmakonPatientIds=${patientIdsString}`);
      };
      result.bffVoidPrimaryInsurance = (insuredId: string) => {
        return bff.post(`api/insurance/${insuredId}/primary/void`);
      };
      result.bffVoidSecondaryInsurance = (insuredId: string, bin: string, cardholderNumber: string) => {
        return bff.post(`api/insurance/${insuredId}/secondary/${bin}/${cardholderNumber}/void`);
      };
      result.checkZipCodeEligibility = (zipCode: string) => {
        return bff.get(checkZipCodeEligibilityUrl(zipCode));
      };
      result.getFacilityByZipCode = (zipCode: string) => {
        return bff.get(`api/facility/facility_by_zip_code/${zipCode}`);
      };
      result.createLead = (body: { email: string; zipCode: string; source: string }) => {
        return bff.post('api/lead/', body);
      };

      result.bffGetOTCItems = (accountId: string) => {
        return bff.get(bffGetOTCItemsUrl(accountId));
      };

      result.bffGetCart = (cartId: string) => {
        return bff.get(bffCartUrl(cartId));
      };

      result.bffGetCartDeliveryMethods = (cartId: string) => bff.get(`/api/v2/carts/${cartId}/delivery_methods`);

      result.bffGetCartDeliveryWindows = (cartId: string) => bff.get(`/api/v2/carts/${cartId}/delivery_windows`);

      result.bffGetCartDeliveryShippingOptions = (cartId: string, deliveryMethod: string) =>
        bff.get(`/api/v2/carts/${cartId}/shipping_options`, {
          params: { deliveryMethod },
        });

      result.bffCreateCart = (body: Cart) => {
        return bff.post(bffCreateCartUrl(), body);
      };

      result.bffSetCartDeliveryAddress = (cartId: string, body: UpdateCartDeliveryAddressRequest) => {
        return bff.put(`api/v2/carts/${cartId}/delivery_address`, body);
      };

      result.bffSetCartDeliveryMethod = (cartId: string, body: UpdateCartDeliveryMethodRequest) => {
        return bff.put(`api/v2/carts/${cartId}/delivery_method`, body);
      };

      result.bffSetCartTip = (cartId: string, body: UpdateCartDeliveryMethodRequest) => {
        return bff.put(`api/v2/carts/${cartId}/tip`, body);
      };

      result.bffSetCartPayment = (cartId: string, body: UpdateCartPaymentMethodRequest) => {
        return bff.put(`api/v2/carts/${cartId}/payment_method`, body);
      };

      result.bffCheckoutCart = (cartId: string, body: any) => {
        return bff.post(bffCheckoutCartUrl(cartId), body);
      };

      /** @deprecated use the react query hook */
      result.getMedicationDetails = (prescriptionId: string, accountId: string) => {
        return bff.get(`api/prescriptions/${prescriptionId}/drug_detail`, {
          params: { isNativeMobile: false, accountId },
        });
      };

      result.getTransferDetails = (prescriptionId: string, accountId: string) => {
        return bff.get(`api/transfers/${prescriptionId}/detail`, {
          params: { isNativeMobile: false, accountId },
        });
      };

      result.getMedicationGuideForNDC = (ndc: string) => {
        return bff.get(`api/prescriptions/${ndc}/medication_guide`);
      };

      result.postOnboarding = (body: any) => {
        return bff.post(`api/onboarding/account_transfers`, body);
      };

      result.getPickupStations = (cartUUID: string) => {
        return bff.get(`api/v2/carts/${cartUUID}/pickup_stations`);
      };

      result.patchDeliveryAddress = (deliveryAddressId: string, body: any) => {
        return bff.patch(`api/delivery_addresses/${deliveryAddressId}`, body);
      };

      result.patchAccount = (accountId: string, body: any) => {
        return bff.patch(`api/accounts/${accountId}`, body);
      };

      result.getIncompleteEscriptCount = (accountId: string) => {
        return bff.get(`api/escript/incomplete_escript_count/${accountId}`);
      };

      result.getMedicationsV3 = (searchTerm: string, zipCode: string) => {
        return bff.get(getMedicationsV3Url(searchTerm, zipCode));
      };

      result.notifyTransfer = (accountId: string) => {
        return bff.post(notifyTransferUrl(accountId));
      };

      result.updateContactInfo = (accountId: string, body: any) => {
        return bff.post(`api/accounts/${accountId}/contact_info`, body);
      };

      result.getChatAuthTicket = () => {
        return bff.post(`api/chat/auth_ticket`);
      };

      result.getChatHistory = (count: number, type: string) => {
        return bff.get(`api/chat/messages`, { params: { count, type } });
      };

      result.markChatAsRead = () => {
        return bff.post(`api/chat/messages/read`, { messages: [] });
      };

      result.getChatUnreadMessagesCount = () => {
        return bff.get(`api/chat/messages/unread_count`);
      };

      result.uploadChatImage = (imageType: 'jpeg' | 'png') => {
        return bff.post(`api/chat/image/upload`, { imageType: imageType });
      };

      result.downloadChatImage = (filepath: string) => {
        return bff.get(`api/chat/image/download`, { params: { filepath } });
      };

      result.checkEmailAvailability = (body: any) => {
        return bff.post(checkEmailAvailabilityUrl(), body);
      };

      result.getHomeBanner = (accountId: string) => {
        return bff.get(`api/banner/home/${accountId}`);
      };

      result.upgradeToken = (token: string) => {
        return bff.get(`api/auth/upgrade_token`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      };

      result.upgradeLegacyToken = (legacyToken: string) => {
        return bff.post(`api/auth/upgrade_legacy_token`, { legacyToken });
      };

      return result;
    }
  );

  return client;
}
